import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PasswordInput } from '../components/ui';

/**
 * Reset password.
 *  - Signed in: just set a new password (authenticated /auth/change-password).
 *  - Signed OUT (forgot password): two steps —
 *      1. enter the account's email → we email a 6-digit code (/auth/email-code/reset);
 *      2. enter the code + new password (/auth/reset-password).
 *    The old version reset ANY account from just its email or phone number, so anyone
 *    who knew an address could take the account over. Phone-only resets are no longer
 *    possible — those users ask support (admin → Users → Reset password).
 */
export default function ForgotPassword() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState('email'); // signed out: 'email' → 'reset'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const sendCode = async () => {
    const res = await api.post('/auth/email-code/reset', { email: email.trim() });
    setInfo(res.data?.message || 'If an account uses this email, we sent it a 6-digit code.');
    setCooldown(60);
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setError('');
    if (!email.includes('@')) {
      setError('Enter the email on your account');
      return;
    }
    setLoading(true);
    try {
      await sendCode();
      setStep('reset');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send the code');
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    setError('');
    try { await sendCode(); } catch (err) { setError(err.response?.data?.message || 'Could not send the code'); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setOk('');
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      const res = user
        ? await api.post('/auth/change-password', { newPassword, confirmPassword })
        : await api.post('/auth/reset-password', { email: email.trim(), code: code.trim(), newPassword, confirmPassword });
      setOk(res.data?.message || 'Password updated');
      setTimeout(() => navigate(user ? '/signals' : '/login'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not update password');
    } finally {
      setLoading(false);
    }
  };

  const askEmail = !user && step === 'email';

  return (
    <div className="flex min-h-[calc(100vh-3.5rem)] flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-6 shadow-card sm:p-8">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-slate-900">
          Reset password
        </h1>
        <p className="mt-1.5 text-sm text-slate-500">
          {user
            ? 'Enter and confirm your new password.'
            : askEmail
              ? "Enter your account's email and we'll send you a 6-digit code."
              : 'Enter the code from your email and choose a new password.'}
        </p>

        {error && (
          <div className="mt-4 rounded-xl border border-rose-100 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{error}</div>
        )}
        {ok && (
          <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{ok}</div>
        )}
        {!user && !askEmail && info && !ok && (
          <div className="mt-4 rounded-xl border border-emerald-100 bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-700">{info}</div>
        )}

        {askEmail ? (
          <form onSubmit={requestCode} className="mt-6 space-y-3.5">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="fs-input"
                placeholder="you@example.com"
                autoComplete="username"
                required
              />
            </div>
            <button type="submit" disabled={loading} className="fs-btn-primary w-full py-3 text-sm">
              {loading ? 'Sending…' : 'Send code'}
            </button>
            <p className="text-center text-[11px] text-slate-400">
              Signed up with a phone number only? Contact support from the Help page.
            </p>
          </form>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-3.5">
            {!user && (
              <div>
                <label className="mb-1.5 block text-xs font-medium text-slate-600">Code from your email</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="fs-input text-center font-mono text-xl tracking-[0.5em]"
                  placeholder="••••••"
                />
              </div>
            )}
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">New password</label>
              <PasswordInput
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="fs-input"
                placeholder="Min 6 characters"
                autoComplete="new-password"
                required
              />
            </div>
            <div>
              <label className="mb-1.5 block text-xs font-medium text-slate-600">Confirm new password</label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="fs-input"
                autoComplete="new-password"
                required
              />
            </div>
            <button
              type="submit"
              disabled={loading || (!user && code.length !== 6)}
              className="fs-btn-primary w-full py-3 text-sm"
            >
              {loading ? 'Updating…' : 'Update password'}
            </button>
            {!user && (
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={() => { setStep('email'); setError(''); }} className="font-medium text-slate-500 hover:text-slate-800">
                  ← Change email
                </button>
                <button type="button" onClick={resend} disabled={cooldown > 0} className="font-medium text-brand-700 hover:underline disabled:text-slate-400 disabled:no-underline">
                  {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
                </button>
              </div>
            )}
          </form>
        )}

        <p className="mt-6 text-center text-sm text-slate-500">
          <Link to={user ? '/signals' : '/login'} className="font-medium text-brand-700 hover:underline">
            {user ? 'Back to signals' : 'Back to login'}
          </Link>
        </p>
      </div>
    </div>
  );
}
